
interface DebouncedSearchOptions {
  delay?: number;
  maxCalls?: number;
  timeWindow?: number;
}

class DebouncedSearch {
  private timeouts = new Map<string, NodeJS.Timeout>();
  private callCounts = new Map<string, { count: number; resetTime: number }>();
  // Monotonic counter per key. clearTimeout only cancels a search that hasn't
  // started yet; once searchFn() is awaited it will still resolve, and without
  // this guard a slow earlier response could land after — and overwrite — a
  // newer one.
  private generations = new Map<string, number>();
  private options: Required<DebouncedSearchOptions>;

  constructor(options: DebouncedSearchOptions = {}) {
    this.options = {
      delay: 150,
      maxCalls: 15,
      timeWindow: 30000, // 30 seconds
      ...options
    };
  }

  async search<T>(
    key: string,
    searchFn: () => Promise<T>,
    callback: (results: T) => void
  ): Promise<void> {
    // Clear existing timeout
    const existingTimeout = this.timeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Supersede any request already in flight for this key.
    const generation = (this.generations.get(key) ?? 0) + 1;
    this.generations.set(key, generation);

    // Check rate limiting
    if (!this.canMakeCall(key)) {
      console.warn(`Rate limit exceeded for search key: ${key}`);
      // Still invoke the callback. Callers clear their loading flag in here,
      // so returning silently left spinners running indefinitely.
      callback([] as unknown as T);
      return;
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      try {
        this.incrementCallCount(key);
        const results = await searchFn();
        if (this.generations.get(key) !== generation) return; // superseded
        callback(results);
      } catch (error) {
        console.error('Debounced search failed:', error);
        if (this.generations.get(key) !== generation) return;
        callback([] as unknown as T);
      } finally {
        // Only clear our own entry — a newer search may have registered since.
        if (this.timeouts.get(key) === timeout) {
          this.timeouts.delete(key);
        }
      }
    }, this.options.delay);

    this.timeouts.set(key, timeout);
  }

  private canMakeCall(key: string): boolean {
    const now = Date.now();
    const callData = this.callCounts.get(key);

    if (!callData || now > callData.resetTime) {
      return true;
    }

    return callData.count < this.options.maxCalls;
  }

  private incrementCallCount(key: string): void {
    const now = Date.now();
    const callData = this.callCounts.get(key);

    if (!callData || now > callData.resetTime) {
      this.callCounts.set(key, {
        count: 1,
        resetTime: now + this.options.timeWindow
      });
    } else {
      callData.count++;
    }
  }

  cancel(key: string): void {
    const timeout = this.timeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(key);
    }
  }

  cancelAll(): void {
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts.clear();
  }

  getCallCount(key: string): number {
    const callData = this.callCounts.get(key);
    if (!callData || Date.now() > callData.resetTime) {
      return 0;
    }
    return callData.count;
  }
}

export const searchDebouncer = new DebouncedSearch();
